const cleanObj = (obj) => {
    if (!obj)
        return obj;
    if (typeof obj !== 'object')
        return obj;
    return Object.fromEntries(Object.entries(obj)
        .filter(([, v]) => !!v)
        .map(([k, v]) => [k, v]));
};
/**
 * Merges custom codes from account, dynamic page, and website levels.
 * - Account level defines base custom codes with their default enabled state
 * - Dynamic page level can add new codes and override enabled state via references
 * - Website level can add new codes and override enabled state via references
 *
 * @param accountConfig - Account level configuration
 * @param dynamicPageConfig - Dynamic page level configuration
 * @param websiteConfig - Website level configuration
 * @returns Array of merged custom codes with their final enabled state
 */
const mergeCustomCodes = (accountConfig, dynamicPageConfig, websiteConfig) => {
    // Start with account-level custom codes
    const customCodesMap = new Map();
    // Add account level custom codes
    if (accountConfig?.customCodes) {
        accountConfig.customCodes.forEach((code) => {
            customCodesMap.set(code.id, { ...code });
        });
    }
    // Add dynamic page level custom codes (new ones only)
    if (dynamicPageConfig?.customCodes) {
        dynamicPageConfig.customCodes.forEach((code) => {
            if (!customCodesMap.has(code.id)) {
                customCodesMap.set(code.id, { ...code });
            }
        });
    }
    // Add website level custom codes (new ones only)
    if (websiteConfig?.customCodes) {
        websiteConfig.customCodes.forEach((code) => {
            if (!customCodesMap.has(code.id)) {
                customCodesMap.set(code.id, { ...code });
            }
        });
    }
    // Apply dynamic page level references (enabled/disabled overrides)
    if (dynamicPageConfig?.customCodesReferences) {
        dynamicPageConfig.customCodesReferences.forEach((ref) => {
            const code = customCodesMap.get(ref.id);
            if (code) {
                code.enabled = ref.enabled;
            }
        });
    }
    // Apply website level references (enabled/disabled overrides)
    if (websiteConfig?.customCodesReferences) {
        websiteConfig.customCodesReferences.forEach((ref) => {
            const code = customCodesMap.get(ref.id);
            if (code) {
                code.enabled = ref.enabled;
            }
        });
    }
    return Array.from(customCodesMap.values());
};
const applyExtras = (websiteData, campaign, brand, config, globalSections) => {
    const applySection = (section) => {
        if (section.global?.globalId && globalSections) {
            const globalSection = globalSections.find((s) => s.id === section.global?.globalId);
            if (globalSection) {
                return {
                    ...section,
                    ...globalSection,
                    id: section.id,
                    global: section.global,
                };
            }
        }
        if (section.type === 'footer' && config) {
            const newLinks = section.links;
            if (config.termsOfUseLink) {
                const termsLink = newLinks.find((l) => l.type === 'terms');
                if (termsLink)
                    termsLink.href = config.termsOfUseLink;
            }
            if (config.privacyPolicyLink) {
                const privacyLink = newLinks.find((l) => l.type === 'privacy');
                if (privacyLink)
                    privacyLink.href = config.privacyPolicyLink;
            }
            if (config.links) {
                newLinks.forEach((l) => {
                    if (l.type === 'terms' || l.type === 'privacy')
                        return;
                    const href = config.links?.[l.type];
                    if (href)
                        l.href = href;
                });
            }
        }
        return section;
    };
    // Merge custom codes from all levels
    let mergedCustomCodes = [];
    try {
        mergedCustomCodes = mergeCustomCodes(config, campaign?.config, websiteData.config);
    }
    catch (error) {
        console.error('Error merging custom codes:', error);
        // Fallback to empty array on error
        mergedCustomCodes = [];
    }
    return {
        ...websiteData,
        sections: websiteData.sections.map(applySection),
        brand: {
            ...brand,
            ...campaign?.brand,
            ...websiteData?.brand,
        },
        config: {
            ...cleanObj(config),
            ...cleanObj(campaign?.config),
            ...cleanObj(websiteData.config),
            customCodes: mergedCustomCodes,
            seo: {
                ...cleanObj(config?.seo),
                ...cleanObj(campaign?.config?.seo),
                ...cleanObj(websiteData.config?.seo),
            },
        },
        language: websiteData.language || campaign?.language,
    };
};
const applyExtraData = (websiteData, campaign, brand, config, globalSections) => {
    return {
        ...websiteData,
        development: applyExtras(websiteData.development, campaign, brand, config, globalSections),
    };
};
const getGlobalSectionsUsed = (website) => {
    const sections = website.development?.sections || [];
    return sections
        .filter((s) => s.global)
        .map((s) => s.global)
        .filter((s) => !!s);
};

export { applyExtraData, applyExtras, getGlobalSectionsUsed, mergeCustomCodes };
//# sourceMappingURL=website.utils.esm.js.map
